import { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  type FeedFilter,
  type FeedItem,
  deleteFeedItem,
  emitFeedItemsChanged,
  markRead,
  markManyRead,
  promoteToLibrary,
  setArchived,
  toggleStar,
} from "@/lib/feedReader";
import { safeHref } from "@/lib/safeHref";

interface Args {
  items: FeedItem[];
  setItems: React.Dispatch<React.SetStateAction<FeedItem[]>>;
  setUnreadCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  filter: FeedFilter;
  selectedItemId: string | null;
  setSelectedItemId: (id: string | null) => void;
  refresh: () => Promise<void>;
}

/**
 * Encapsulates per-item mutations on the feed reader page (star/archive/read,
 * delete, promote, mark-all-read, open-external). All handlers update local
 * state optimistically and reconcile unread counts.
 */
export function useFeedItemActions({
  items,
  setItems,
  setUnreadCounts,
  filter,
  selectedItemId,
  setSelectedItemId,
  refresh,
}: Args) {
  const [actingItemId, setActingItemId] = useState<string | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const optimisticUpdate = useCallback(
    (id: string, patch: Partial<FeedItem>) => {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    },
    [setItems],
  );

  const handleStar = useCallback(
    async (item: FeedItem) => {
      const next = !item.starred_at;
      optimisticUpdate(item.id, { starred_at: next ? new Date().toISOString() : null });
      try {
        await toggleStar(item.id, next);
      } catch (err) {
        optimisticUpdate(item.id, { starred_at: item.starred_at });
        toast.error((err as Error).message);
      }
    },
    [optimisticUpdate],
  );

  const handleArchive = useCallback(
    async (item: FeedItem) => {
      const next = !item.archived_at;
      optimisticUpdate(item.id, { archived_at: next ? new Date().toISOString() : null });
      try {
        await setArchived(item.id, next);
        if ((filter === "unread" && next) || (filter === "archive" && !next)) {
          setItems((prev) => prev.filter((it) => it.id !== item.id));
        }
        if (next && !item.read_at) {
          setUnreadCounts((prev) => ({
            ...prev,
            [item.subscription_id]: Math.max(0, (prev[item.subscription_id] ?? 1) - 1),
          }));
          emitFeedItemsChanged();
        } else if (!next && !item.read_at) {
          setUnreadCounts((prev) => ({
            ...prev,
            [item.subscription_id]: (prev[item.subscription_id] ?? 0) + 1,
          }));
          emitFeedItemsChanged();
        }
      } catch (err) {
        optimisticUpdate(item.id, { archived_at: item.archived_at });
        toast.error((err as Error).message);
      }
    },
    [filter, optimisticUpdate, setItems, setUnreadCounts],
  );

  const handleToggleRead = useCallback(
    async (item: FeedItem) => {
      const next = !item.read_at;
      optimisticUpdate(item.id, { read_at: next ? new Date().toISOString() : null });
      setUnreadCounts((prev) => ({
        ...prev,
        [item.subscription_id]: Math.max(0, (prev[item.subscription_id] ?? 0) + (next ? -1 : 1)),
      }));
      try {
        await markRead(item.id, next);
        if (filter === "unread" && next) {
          setItems((prev) => prev.filter((it) => it.id !== item.id));
        }
        emitFeedItemsChanged();
      } catch (err) {
        optimisticUpdate(item.id, { read_at: item.read_at });
        toast.error((err as Error).message);
      }
    },
    [filter, optimisticUpdate, setItems, setUnreadCounts],
  );

  const handleOpenExternal = useCallback(
    (item: FeedItem) => {
      const safeLink = safeHref(item.link);
      if (!safeLink) return;
      if (!item.read_at) {
        optimisticUpdate(item.id, { read_at: new Date().toISOString() });
        void markRead(item.id, true).catch(() => undefined);
        if (filter === "unread") setItems((prev) => prev.filter((it) => it.id !== item.id));
        setUnreadCounts((prev) => ({
          ...prev,
          [item.subscription_id]: Math.max(0, (prev[item.subscription_id] ?? 1) - 1),
        }));
        emitFeedItemsChanged();
      }
      window.open(safeLink, "_blank", "noopener,noreferrer");
    },
    [filter, optimisticUpdate, setItems, setUnreadCounts],
  );

  const handleMarkAllRead = useCallback(
    async (subscriptionId?: string) => {
      const targetIds = items
        .filter((it) => !it.read_at && !it.archived_at && (!subscriptionId || it.subscription_id === subscriptionId))
        .map((it) => it.id);
      if (!targetIds.length) {
        toast.message("Nothing to mark");
        return;
      }
      setMarkingAllRead(true);
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((it) => (targetIds.includes(it.id) ? { ...it, read_at: now } : it)),
      );
      try {
        await markManyRead(targetIds, true);
        setUnreadCounts((prev) => {
          const next = { ...prev };
          for (const it of items) {
            if (targetIds.includes(it.id)) {
              next[it.subscription_id] = Math.max(0, (next[it.subscription_id] ?? 1) - 1);
            }
          }
          return next;
        });
        if (filter === "unread") {
          setItems((prev) => prev.filter((it) => !targetIds.includes(it.id)));
        }
        emitFeedItemsChanged();
        toast.success(`Marked ${targetIds.length} as read`);
      } catch (err) {
        toast.error((err as Error).message);
        await refresh();
      } finally {
        setMarkingAllRead(false);
      }
    },
    [items, filter, setItems, setUnreadCounts, refresh],
  );

  const handlePromote = useCallback(
    async (item: FeedItem) => {
      setActingItemId(item.id);
      try {
        const r = await promoteToLibrary(item.id);
        optimisticUpdate(item.id, {
          imported_to_library_item_id: r.library_item_id,
          imported_at: new Date().toISOString(),
          read_at: item.read_at ?? new Date().toISOString(),
        });
        toast.success(r.already_imported ? "Already in My Library" : "Saved to My Library");
      } catch (err) {
        toast.error((err as Error).message);
      } finally {
        setActingItemId(null);
      }
    },
    [optimisticUpdate],
  );

  const handleDeleteItem = useCallback(
    async (item: FeedItem) => {
      if (!window.confirm("Delete this item from your feed? It will not come back on the next sync.")) return;
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      if (selectedItemId === item.id) setSelectedItemId(null);
      if (!item.read_at && !item.archived_at) {
        setUnreadCounts((prev) => ({
          ...prev,
          [item.subscription_id]: Math.max(0, (prev[item.subscription_id] ?? 1) - 1),
        }));
        emitFeedItemsChanged();
      }
      try {
        await deleteFeedItem(item.id);
      } catch (err) {
        toast.error((err as Error).message);
      }
    },
    [selectedItemId, setItems, setSelectedItemId, setUnreadCounts],
  );

  return {
    actingItemId,
    markingAllRead,
    optimisticUpdate,
    handleStar,
    handleArchive,
    handleToggleRead,
    handleOpenExternal,
    handleMarkAllRead,
    handlePromote,
    handleDeleteItem,
  };
}
